import {
  EXTENSION_URL,
  EXTENSIONS_SELF_REPORTED,
  VITAL_SIGNS_CLINICAL_NON_CLINICAL,
} from '@constants/extension-codes';
import { GENETIC_INFO_TYPE_MAP } from '@constants/option-types/genetic-info';
import {
  COMPLETE_LAB_OBSERVATION_CODE_MAP,
  LAB_REPORT_NAME_MAP,
} from '@constants/option-types/lab';
import { ALL_OBSERVATION_OPTIONS_MAP } from '@constants/option-types/vital-signs';

import type { GeneticInfoValues } from '@models/genetic-information';
import type { LabObservationValues } from '@models/lab-result';
import {
  ObservationRecord,
  type ReducedObservationComponent,
} from '@models/observation';
import type { VitalSignValues } from '@models/vital-sign';

import { formatPatientId } from '@utils/format';
import { isBooleanValue, isNumberValue } from '@utils/lab';

import type {
  Extension,
  ObservationComponent,
  ObservationModelInput,
} from 'generated/types';

export type ObservationType =
  'body' | 'fitness' | 'genetic-information' | 'laboratory' | 'vital-signs';

export const OBSERVATION_TYPE_MAP: Record<
  ObservationType,
  { display: string }
> = {
  'genetic-information': { display: 'Genetic Information' },
  fitness: { display: 'Fitness' },
  laboratory: { display: 'Lab Results' },
  'vital-signs': { display: 'Vital Signs' },
  body: { display: 'Body' },
};

export function getObservationTemplate(
  patient_id: string,
  values: GeneticInfoValues,
  type: ObservationType
): ObservationModelInput {
  const extensions: Extension[] = [];
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensions.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  return {
    resourceType: 'Observation',
    status: 'final',
    subject: {
      reference: formatPatientId(patient_id),
    },
    // CATEGORY: genetic-information, laboratory, vital sign, body
    category: [
      {
        coding: [
          {
            code: type,
            display: OBSERVATION_TYPE_MAP[type].display,
            system: ObservationRecord.VITALL_SYSTEM,
          },
        ],
        text: OBSERVATION_TYPE_MAP[type].display,
      },
    ],
    code: {
      coding: [
        {
          code: values.code,
          display: values.code && GENETIC_INFO_TYPE_MAP[values.code],
          system: ObservationRecord.VITALL_SYSTEM,
        },
      ],
      text: values.code && GENETIC_INFO_TYPE_MAP[values.code],
    },
    ...(values.testDate && {
      effectiveDateTime: values.testDate,
    }),
    ...(type === 'genetic-information' &&
      values.title && {
        valueString: values.title,
      }),
    ...(extensions.length && {
      extension: extensions,
    }),
  };
}

export function getLabObservationTemplate(
  patient_id: string,
  values: LabObservationValues
): ObservationModelInput {
  const numberValue = isNumberValue(values.value.toString());
  const booleanValue = isBooleanValue(values.value);

  return {
    resourceType: 'Observation',
    status: 'final',
    subject: {
      reference: formatPatientId(patient_id),
    },
    // Lab report type
    category: [
      {
        coding: [
          {
            code: values.category,
            display: values.category && LAB_REPORT_NAME_MAP[values.category],
            system: ObservationRecord.VITALL_SYSTEM,
          },
        ],
        text: values.category && LAB_REPORT_NAME_MAP[values.category],
      },
    ],
    // Lab observation type
    code: {
      coding: [
        {
          code: values.code,
          display:
            values.code in COMPLETE_LAB_OBSERVATION_CODE_MAP
              ? COMPLETE_LAB_OBSERVATION_CODE_MAP[values.code]
              : values.displayName,
          system: ObservationRecord.VITALL_SYSTEM,
        },
      ],
      text:
        values.code in COMPLETE_LAB_OBSERVATION_CODE_MAP
          ? COMPLETE_LAB_OBSERVATION_CODE_MAP[values.code]
          : values.displayName,
    },
    ...(values.testDate && {
      effectiveDateTime: values.testDate,
    }),
    ...(booleanValue && {
      valueBoolean: Boolean(
        values.value === 'Positive' ||
        values.value === true ||
        values.value === 'true'
      ),
    }),
    ...(values.value &&
      numberValue && {
        valueQuantity: {
          unit: values.unit,
          value: Number(values.value),
        },
      }),
    ...(values.value &&
      !booleanValue &&
      !numberValue && {
        valueString: values.value.toString(),
      }),
  };
}

/**
 * Process an array of vital sign components into FHIR properties
 * @param components Vital sign components/readings
 * @returns valueQuantity object if component length is 1, otherwise an array of observation components
 */
function processComponents(
  components: ReducedObservationComponent[]
): Partial<Pick<ObservationModelInput, 'component' | 'valueQuantity'>> {
  if (components.length === 1) {
    const [component] = components;
    return {
      valueQuantity: {
        ...(component.unit && {
          unit: component.unit,
        }),
        code: component.code,
        value: component.value,
        system: ObservationRecord.VITALL_SYSTEM,
      },
    };
  }
  return {
    component: components.map<ObservationComponent>(
      ({ code, display, unit, value }) => {
        return {
          code: {
            coding: [
              {
                code,
                display,
              },
            ],
            text: display,
          },
          valueQuantity: {
            system: ObservationRecord.VITALL_SYSTEM,
            code,
            unit,
            value,
          },
        };
      }
    ),
  };
}

export function getVitalSignTemplate(
  patient_id: string,
  values: VitalSignValues & {
    components: ReducedObservationComponent[];
  },
  category: ObservationType
): ObservationModelInput {
  const display = OBSERVATION_TYPE_MAP[category].display;
  return {
    resourceType: 'Observation',
    status: 'final',
    subject: {
      reference: formatPatientId(patient_id),
    },
    // Categories: Vital Sign, Body
    category: [
      {
        coding: [
          {
            code: category,
            display,
            system: ObservationRecord.VITALL_SYSTEM,
          },
        ],
        text: display,
      },
    ],
    code: {
      coding: [
        {
          code: values.code,
          display: ALL_OBSERVATION_OPTIONS_MAP[values.code].display,
          system: ObservationRecord.VITALL_SYSTEM,
        },
      ],
      text: ALL_OBSERVATION_OPTIONS_MAP[values.code].display,
    },
    ...(values.testDate && {
      effectiveDateTime: values.testDate,
    }),
    ...(values.source && {
      performer: [
        {
          display: values.source,
        },
      ],
    }),
    ...((values.selfReported || values.clinicalStatus) && {
      extension: [
        {
          url: `${EXTENSION_URL}${VITAL_SIGNS_CLINICAL_NON_CLINICAL}`,
          valueBoolean: values.clinicalStatus,
        },
        ...(values.selfReported
          ? [
              {
                url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
                valueBoolean: values.selfReported,
              },
            ]
          : []),
      ],
    }),
    ...processComponents(values.components),
  };
}

export function getBloodTypeObservationTemplate(
  patient_id: string,
  bloodType: string,
  createdDate?: string
): ObservationModelInput {
  return {
    resourceType: 'Observation',
    status: 'final',
    subject: {
      reference: formatPatientId(patient_id),
    },
    category: [
      {
        coding: [
          {
            code: 'laboratory',
            display: 'Laboratory',
            system:
              'http://terminology.hl7.org/CodeSystem/observation-category',
          },
        ],
        text: 'Laboratory',
      },
    ],
    code: {
      coding: [
        {
          code: 'blood-type',
          display: 'Blood Type',
          system: ObservationRecord.VITALL_SYSTEM,
        },
      ],
      text: 'Blood Type',
    },
    ...(createdDate && {
      effectiveDateTime: createdDate,
    }),
    valueString: bloodType,
  };
}
